/**
 * motion.ts — Registro de movimento declarativo do Studio (Fase 5)
 *
 * Módulo NEUTRO (sem DOM, React, Node ou Deno). É a única fonte de verdade das
 * animações: os padrões e os blocos passam a REFERENCIAR um movimento pela
 * chave, em vez de carregar CSS de animação solto. O mesmo "subir surgindo"
 * tem o mesmo desenho no editor, na página publicada, no PDF e no e-mail — e,
 * quando o meio não suporta (impressão, e-mail), simplesmente não anima.
 *
 * Três garantias desta fase:
 *  1. CONTRATO — todo movimento declara versão, meios suportados, substituto e
 *     custo de renderização (Matriz de Capacidades);
 *  2. SEM JAVASCRIPT — o disparo por rolagem usa `animation-timeline: view()`,
 *     recurso nativo do navegador. A página pública continua sem script algum;
 *  3. ORÇAMENTO DE INTERAÇÃO — uma página tem um teto de animações. Passou do
 *     teto, o excedente sai parado. Isso evita a "página pisca-pisca" e protege
 *     o desempenho em celulares.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - Miniatura nunca anima (motor de composições, Fase 7)
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (motor de composições, Fase 5)
 */

/** Modos de saída atendidos pelo Studio. */
export type StudioMotionRenderMode = 'editor' | 'published' | 'thumbnail' | 'pdf' | 'email';

/** Custo aproximado de renderização do movimento. */
export type StudioMotionCost = 'low' | 'medium' | 'high';

/** Quando a animação acontece. */
export type StudioMotionTrigger = 'load' | 'scroll';

/** Contrato de um movimento. */
export interface StudioMotionDefinition {
  key: string;
  label: string;
  /** Frase curta para o painel de propriedades. */
  description?: string;
  version: string;
  supportedRenderModes: StudioMotionRenderMode[];
  /** Movimento usado quando o meio não suporta este. `null` = sem movimento. */
  fallback: string | null;
  performanceCost: StudioMotionCost;
  /** Repete para sempre (conta no teto de laços do orçamento). */
  loop?: boolean;
  /** Quadros da animação (`@keyframes`). */
  keyframes: string;
  /** Corpo da regra `.st-m-<chave>`. */
  css: string;
}

// Movimento é coisa de tela: impressão e e-mail nunca animam.
const TELA: StudioMotionRenderMode[] = ['editor', 'published'];

/** Teto de animações por página (orçamento de interação). */
export const STUDIO_MOTION_BUDGET = { total: 40, loops: 3 } as const;

/**
 * Catálogo de movimentos.
 * Para acrescentar um movimento novo basta registrar aqui: painel, CSS do
 * editor e compilador HTML passam a conhecê-lo sem nenhuma outra alteração.
 */
const MOVIMENTOS: StudioMotionDefinition[] = [
  {
    key: 'none',
    label: 'Nenhum',
    version: '1.0.0',
    supportedRenderModes: ['editor', 'published', 'thumbnail', 'pdf', 'email'],
    fallback: null,
    performanceCost: 'low',
    keyframes: '',
    css: '',
  },
  {
    key: 'fade',
    label: 'Surgir',
    description: 'O elemento aparece suavemente.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: null,
    performanceCost: 'low',
    keyframes: '@keyframes st-fade { from { opacity: 0; } to { opacity: 1; } }',
    css: 'animation: st-fade var(--st-m-dur, .7s) ease var(--st-m-delay, 0s) both;',
  },
  {
    key: 'fade-up',
    label: 'Subir surgindo',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'low',
    keyframes:
      '@keyframes st-fade-up { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }',
    css: 'animation: st-fade-up var(--st-m-dur, .7s) cubic-bezier(.22,1,.36,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'fade-down',
    label: 'Descer surgindo',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'low',
    keyframes:
      '@keyframes st-fade-down { from { opacity: 0; transform: translateY(-18px); } to { opacity: 1; transform: none; } }',
    css: 'animation: st-fade-down var(--st-m-dur, .7s) cubic-bezier(.22,1,.36,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'slide',
    label: 'Deslizar da esquerda',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'low',
    keyframes:
      '@keyframes st-slide { from { opacity: 0; transform: translateX(-22px); } to { opacity: 1; transform: none; } }',
    css: 'animation: st-slide var(--st-m-dur, .7s) cubic-bezier(.22,1,.36,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'slide-right',
    label: 'Deslizar da direita',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'low',
    keyframes:
      '@keyframes st-slide-right { from { opacity: 0; transform: translateX(22px); } to { opacity: 1; transform: none; } }',
    css: 'animation: st-slide-right var(--st-m-dur, .7s) cubic-bezier(.22,1,.36,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'scale',
    label: 'Ampliar',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'low',
    keyframes:
      '@keyframes st-scale { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }',
    css: 'animation: st-scale var(--st-m-dur, .7s) cubic-bezier(.22,1,.36,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'reveal',
    label: 'Revelar',
    description: 'O conteúdo é descoberto da esquerda para a direita.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'medium',
    keyframes:
      '@keyframes st-reveal { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }',
    css: 'animation: st-reveal var(--st-m-dur, .9s) cubic-bezier(.65,0,.35,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'blur-in',
    label: 'Sair do desfoque',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'medium',
    keyframes:
      '@keyframes st-blur-in { from { opacity: 0; filter: blur(12px); } to { opacity: 1; filter: blur(0); } }',
    css: 'animation: st-blur-in var(--st-m-dur, .8s) ease var(--st-m-delay, 0s) both;',
  },
  {
    key: 'floating',
    label: 'Flutuar',
    description: 'Sobe e desce sem parar. Use com moderação.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: null,
    performanceCost: 'medium',
    loop: true,
    keyframes:
      '@keyframes st-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }',
    css: 'animation: st-float 6s ease-in-out infinite;',
  },
  {
    key: 'pulse-soft',
    label: 'Pulsar suave',
    description: 'Brilho que respira. Use com moderação.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: null,
    performanceCost: 'medium',
    loop: true,
    keyframes: '@keyframes st-pulse-soft { 0%, 100% { opacity: 1; } 50% { opacity: .72; } }',
    css: 'animation: st-pulse-soft 3.4s ease-in-out infinite;',
  },

  // ---------------------------------------------------------------------------
  // Movimentos da biblioteca própria (Fase 2) — todos em CSS, sem JavaScript.
  // ---------------------------------------------------------------------------
  {
    key: 'stagger',
    label: 'Em cascata',
    description: 'Os itens de dentro aparecem em sequência, um após o outro.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'low',
    // O campo de quadros também aceita as regras dos filhos: é aqui que a
    // cascata ganha o atraso crescente, sem precisar marcar item por item.
    keyframes:
      '@keyframes st-stagger { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }\n' +
      '      .st-m-stagger > * { animation: st-stagger var(--st-m-dur, .6s) cubic-bezier(.22,1,.36,1) both; }\n' +
      '      .st-m-stagger > *:nth-child(1) { animation-delay: calc(var(--st-m-delay, 0s) + 0ms); }\n' +
      '      .st-m-stagger > *:nth-child(2) { animation-delay: calc(var(--st-m-delay, 0s) + 90ms); }\n' +
      '      .st-m-stagger > *:nth-child(3) { animation-delay: calc(var(--st-m-delay, 0s) + 180ms); }\n' +
      '      .st-m-stagger > *:nth-child(4) { animation-delay: calc(var(--st-m-delay, 0s) + 270ms); }\n' +
      '      .st-m-stagger > *:nth-child(5) { animation-delay: calc(var(--st-m-delay, 0s) + 360ms); }\n' +
      '      .st-m-stagger > *:nth-child(6) { animation-delay: calc(var(--st-m-delay, 0s) + 450ms); }\n' +
      '      .st-m-stagger > *:nth-child(n+7) { animation-delay: calc(var(--st-m-delay, 0s) + 540ms); }',
    css: '',
  },
  {
    key: 'mask-up',
    label: 'Revelar de baixo',
    description: 'O conteúdo é descoberto de baixo para cima, como uma cortina.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade-up',
    performanceCost: 'medium',
    keyframes:
      '@keyframes st-mask-up { from { clip-path: inset(100% 0 0 0); transform: translateY(12px); } to { clip-path: inset(0 0 0 0); transform: none; } }',
    css: 'animation: st-mask-up var(--st-m-dur, .9s) cubic-bezier(.65,0,.35,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'zoom-out',
    label: 'Aproximar e assentar',
    description: 'Começa levemente ampliado e assenta no lugar.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade',
    performanceCost: 'low',
    keyframes:
      '@keyframes st-zoom-out { from { opacity: 0; transform: scale(1.08); } to { opacity: 1; transform: none; } }',
    css: 'animation: st-zoom-out var(--st-m-dur, .9s) cubic-bezier(.22,1,.36,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'tilt-in',
    label: 'Entrar inclinado',
    description: 'Entra com leve perspectiva, bom para telas de aplicativo.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: 'fade-up',
    performanceCost: 'medium',
    keyframes:
      '@keyframes st-tilt-in { from { opacity: 0; transform: perspective(1200px) rotateX(12deg) translateY(26px); } to { opacity: 1; transform: none; } }',
    css: 'animation: st-tilt-in var(--st-m-dur, .9s) cubic-bezier(.22,1,.36,1) var(--st-m-delay, 0s) both;',
  },
  {
    key: 'parallax',
    label: 'Paralaxe leve',
    description: 'Sobe devagar conforme a página rola. Sem script.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: null,
    performanceCost: 'medium',
    keyframes:
      '@keyframes st-parallax { from { transform: translateY(34px); } to { transform: translateY(-34px); } }\n' +
      '      @supports (animation-timeline: view()) { .st-m-parallax { animation-timeline: view(); animation-range: cover 0% cover 100%; } }',
    // Sem suporte a linha do tempo de rolagem, o bloco fica simplesmente parado.
    css: 'animation: st-parallax linear both; animation-timeline: auto; animation-duration: 0s;',
  },
  {
    key: 'shine',
    label: 'Brilho passando',
    description: 'Um reflexo atravessa o bloco. Ótimo em botões e cartões.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: null,
    performanceCost: 'medium',
    loop: true,
    keyframes:
      '@keyframes st-shine { 0% { transform: translateX(-120%); } 60%, 100% { transform: translateX(220%); } }\n' +
      '      .st-m-shine { position: relative; overflow: hidden; }\n' +
      '      .st-m-shine::after { content: ""; position: absolute; top: 0; bottom: 0; width: 45%; pointer-events: none;' +
      ' background: linear-gradient(100deg, transparent, rgba(255,255,255,.38), transparent);' +
      ' animation: st-shine 4.5s ease-in-out infinite; }',
    css: '',
  },
  {
    key: 'border-flow',
    label: 'Borda em movimento',
    description: 'Contorno em degradê que gira devagar.',
    version: '1.0.0',
    supportedRenderModes: TELA,
    fallback: null,
    performanceCost: 'high',
    loop: true,
    keyframes:
      '@keyframes st-border-flow { to { --st-angle: 360deg; } }\n' +
      '      @property --st-angle { syntax: "<angle>"; initial-value: 0deg; inherits: false; }',
    css:
      'border: 1px solid transparent; border-radius: 16px;' +
      ' background: linear-gradient(var(--st-surface, #fff), var(--st-surface, #fff)) padding-box,' +
      ' conic-gradient(from var(--st-angle), var(--st-bg-1, #2563eb), var(--st-bg-2, #7c3aed), var(--st-bg-1, #2563eb)) border-box;' +
      ' animation: st-border-flow 8s linear infinite;',
  },
];

const MAPA = new Map<string, StudioMotionDefinition>(MOVIMENTOS.map((m) => [m.key, m]));

/** Atrasos disponíveis, em milissegundos. */
export const STUDIO_MOTION_DELAYS = [0, 100, 200, 300, 400, 600, 800] as const;

/** Durações disponíveis. */
export const STUDIO_MOTION_DURATIONS: Array<{ key: string; label: string; value: string }> = [
  { key: 'fast', label: 'Rápida', value: '.45s' },
  { key: 'normal', label: 'Normal', value: '.7s' },
  { key: 'slow', label: 'Lenta', value: '1.1s' },
];

/** Definição de um movimento pela chave. */
export function getMotion(key: string): StudioMotionDefinition | undefined {
  return MAPA.get(key);
}

/** Catálogo completo (cópia). */
export function listMotions(): StudioMotionDefinition[] {
  return [...MOVIMENTOS];
}

/** Opções prontas para o painel de propriedades. */
export const STUDIO_MOTION_OPTIONS = MOVIMENTOS.map((m) => ({ value: m.key, label: m.label }));

export const STUDIO_MOTION_TRIGGER_OPTIONS = [
  { value: 'load', label: 'Ao abrir a página' },
  { value: 'scroll', label: 'Ao rolar até o bloco' },
];

export const STUDIO_MOTION_DELAY_OPTIONS = STUDIO_MOTION_DELAYS.map((ms) => ({
  value: String(ms),
  label: ms === 0 ? 'Sem atraso' : `${ms} ms`,
}));

export const STUDIO_MOTION_DURATION_OPTIONS = STUDIO_MOTION_DURATIONS.map((d) => ({
  value: d.key,
  label: d.label,
}));

/**
 * Resolve o movimento para o meio de saída, seguindo a corrente de substitutos.
 * Devolve `null` quando nada se aplica (impressão, e-mail, chave desconhecida).
 */
export function resolveMotion(
  key: string | undefined | null,
  mode: StudioMotionRenderMode = 'published',
): StudioMotionDefinition | null {
  // Miniatura NUNCA anima (Fase 7).
  if (mode === 'thumbnail') return null;
  let atual = key ? MAPA.get(String(key)) : undefined;
  for (let salto = 0; salto < 5; salto += 1) {
    if (!atual || atual.key === 'none') return null;
    if (atual.supportedRenderModes.includes(mode)) return atual;
    atual = atual.fallback ? MAPA.get(atual.fallback) : undefined;
  }
  return null;
}

/** Lê a chave de movimento das propriedades do bloco. */
export function lerMovimento(props: unknown): string | null {
  if (!props || typeof props !== 'object') return null;
  const valor = (props as Record<string, unknown>).motion;
  return typeof valor === 'string' && valor && valor !== 'none' ? valor : null;
}

function lerTexto(props: unknown, chave: string): string {
  if (!props || typeof props !== 'object') return '';
  const valor = (props as Record<string, unknown>)[chave];
  return valor == null ? '' : String(valor);
}

/**
 * Orçamento de interação de uma página.
 * Cada renderização cria o seu; ao passar do teto, os blocos seguintes saem sem
 * movimento (o conteúdo continua visível — nada é escondido).
 */
export interface StudioMotionBudget {
  /** Classes de movimento do bloco (string vazia quando não anima). */
  classes(props: unknown): string;
  /** Quantas animações foram concedidas até agora. */
  readonly usados: number;
  /** Quantas foram recusadas por estouro de teto. */
  readonly recusados: number;
}

/** Cria o orçamento de uma renderização. */
export function criarOrcamentoDeMovimento(
  mode: StudioMotionRenderMode = 'published',
  limites: { total?: number; loops?: number } = {},
): StudioMotionBudget {
  const tetoTotal = limites.total ?? STUDIO_MOTION_BUDGET.total;
  const tetoLoops = limites.loops ?? STUDIO_MOTION_BUDGET.loops;
  let usados = 0;
  let laços = 0;
  let recusados = 0;

  return {
    get usados() {
      return usados;
    },
    get recusados() {
      return recusados;
    },
    classes(props: unknown): string {
      const definicao = resolveMotion(lerMovimento(props), mode);
      if (!definicao) return '';
      if (usados >= tetoTotal || (definicao.loop && laços >= tetoLoops)) {
        recusados += 1;
        return '';
      }
      usados += 1;
      if (definicao.loop) laços += 1;

      const partes = [`st-m-${definicao.key}`];

      // Disparo por rolagem: 100% CSS (`animation-timeline: view()`). Onde o
      // navegador não suportar, a animação simplesmente roda ao abrir.
      if (lerTexto(props, 'motionTrigger') === 'scroll' && !definicao.loop) {
        partes.push('st-m-scroll');
      }

      const atraso = Number(lerTexto(props, 'motionDelay') || 0);
      if (!definicao.loop && STUDIO_MOTION_DELAYS.includes(atraso as never) && atraso > 0) {
        partes.push(`st-m-dl-${atraso}`);
      }

      const duracao = lerTexto(props, 'motionDuration');
      if (!definicao.loop && STUDIO_MOTION_DURATIONS.some((d) => d.key === duracao) && duracao !== 'normal') {
        partes.push(`st-m-du-${duracao}`);
      }

      return partes.join(' ');
    },
  };
}

/** CSS de todos os movimentos, emitido pelo próprio catálogo. */
export function motionStyles(): string {
  const quadros = MOVIMENTOS.filter((m) => m.keyframes)
    .map((m) => `      ${m.keyframes}`)
    .join('\n');

  const regras = MOVIMENTOS.filter((m) => m.css)
    .map((m) => `      .st-m-${m.key} { ${m.css} }`)
    .join('\n');

  const atrasos = STUDIO_MOTION_DELAYS.filter((ms) => ms > 0)
    .map((ms) => `      .st-m-dl-${ms} { --st-m-delay: ${ms / 1000}s; }`)
    .join('\n');

  const duracoes = STUDIO_MOTION_DURATIONS.map(
    (d) => `      .st-m-du-${d.key} { --st-m-dur: ${d.value}; }`,
  ).join('\n');

  const todas = MOVIMENTOS.filter((m) => m.css)
    .map((m) => `.st-m-${m.key}`)
    .join(', ');

  return `${quadros}
${regras}
${atrasos}
${duracoes}
      /* Disparo por rolagem sem nenhum JavaScript. */
      @supports (animation-timeline: view()) {
        .st-m-scroll { animation-timeline: view(); animation-range: entry 8% cover 34%; animation-fill-mode: both; }
      }
      /* Acessibilidade e impressão: nada se mexe. */
      @media (prefers-reduced-motion: reduce) {
        ${todas} { animation: none !important; }
      }
      @media print {
        ${todas} { animation: none !important; }
      }`;
}
